import { isFgEventTransitivePropagationStopped, createFgEventBus, flushFgEvents, subscribeFgEvent, pumpFgEventHandlers } from './event-bus.js';
import { getBlockDef } from './block-registry.js';
import { defaultForType, coerceValue } from './rich-type.js';
import { FgEventType } from './types.js';

function getDataValue(ctx, env, block, socket) {
  const input = block.dataIn.find((s) => s.name === socket);
  if (!input) {
    return void 0;
  }
  let raw;
  if (input.source) {
    const producer = blockById(env.graph, input.source.blockId);
    if (producer) {
      const guardKey = `${producer.id}:resolving`;
      if (ctx.executionVariables[guardKey]) {
        raw = input.defaultValue ?? defaultForType(input.type);
      } else {
        const def = env.defs[producer.type];
        ctx.executionVariables[guardKey] = true;
        try {
          def?.updateOutputs?.(producer, ctx, env);
        } finally {
          ctx.executionVariables[guardKey] = false;
        }
        const slot = `${producer.id}:${input.source.socket}`;
        raw = slot in ctx.connectionValues ? ctx.connectionValues[slot] : input.defaultValue ?? defaultForType(input.type);
        if (input.source.scale !== void 0 && typeof raw === "number") {
          raw *= input.source.scale;
        }
      }
    } else {
      raw = input.defaultValue ?? defaultForType(input.type);
    }
  } else {
    raw = input.defaultValue ?? defaultForType(input.type);
  }
  return coerceValue(raw, input.type);
}
function setDataValue(ctx, block, socket, value) {
  ctx.connectionValues[`${block.id}:${socket}`] = value;
}
function getExecVar(ctx, block, key, def) {
  const slot = `${block.id}:${key}`;
  return slot in ctx.executionVariables ? ctx.executionVariables[slot] : def;
}
function setExecVar(ctx, block, key, value) {
  ctx.executionVariables[`${block.id}:${key}`] = value;
}
function activateSignal(ctx, env, block, socket) {
  const output = block.signalOut.find((s) => s.name === socket);
  if (!output || isFgEventTransitivePropagationStopped(env.events)) {
    return;
  }
  for (const target of output.targets) {
    const targetBlock = blockById(env.graph, target.blockId);
    if (!targetBlock) {
      continue;
    }
    env.defs[targetBlock.type]?.execute?.(targetBlock, ctx, env, target.socket);
    if (isFgEventTransitivePropagationStopped(env.events)) {
      break;
    }
  }
}
function addPending(ctx, block, state = {}) {
  const task = {
    blockId: block.id,
    token: ctx._tokenSeq++,
    canceled: false,
    done: false,
    state
  };
  ctx.pending.push(task);
  return task;
}
function stillPending(ctx, task) {
  return !task.canceled && !task.done && ctx.pending.indexOf(task) >= 0;
}
function cancelPendingForBlock(ctx, block) {
  for (const task of ctx.pending) {
    if (task.blockId === block.id) {
      task.canceled = true;
    }
  }
}
function compactPending(ctx) {
  let write = 0;
  for (let read = 0; read < ctx.pending.length; read++) {
    const task = ctx.pending[read];
    if (task && !task.canceled && !task.done) {
      ctx.pending[write++] = task;
    }
  }
  ctx.pending.length = write;
}
function createFgContext(graph, opts) {
  const userVariables = {};
  for (const name of Object.keys(graph.variables)) {
    userVariables[name] = graph.variables[name].value;
  }
  return {
    connectionValues: {},
    executionVariables: {},
    userVariables,
    pending: [],
    rightHanded: opts?.rightHanded ?? false,
    _tokenSeq: 0
  };
}
async function createFgEnv(graph, wiring = {}) {
  const defs = {};
  const unsupported = [];
  for (const block of graph.blocks) {
    if (defs[block.type]) {
      continue;
    }
    const override = wiring.defs?.[block.type];
    if (override) {
      defs[block.type] = override;
      continue;
    }
    const loader = getBlockDef(block.type);
    if (!loader) {
      if (!unsupported.includes(block.type)) {
        unsupported.push(block.type);
      }
      continue;
    }
    defs[block.type] = await loader();
  }
  if (unsupported.length > 0) {
    throw new Error(`flow-graph: unsupported block type(s): ${unsupported.join(", ")}`);
  }
  return {
    graph,
    defs,
    accessors: wiring.accessors ?? {},
    resolveAccessor: wiring.resolveAccessor,
    animations: wiring.animations ?? [],
    caps: wiring.caps ?? {},
    events: wiring.events ?? createFgEventBus(),
    _assetScope: wiring._assetScope
  };
}
async function createFgRuntime(graph, wiring = {}, opts) {
  const env = await createFgEnv(graph, wiring);
  const context = createFgContext(graph, opts);
  return { graph, context, env, started: false, _unsub: [] };
}
function eventInitPriority(event) {
  switch (event) {
    case FgEventType.CustomEvent:
      return 0;
    case FgEventType.Pointer:
    case FgEventType.Key:
      return 1;
    case FgEventType.Tick:
      return 2;
    case FgEventType.Start:
      return 3;
    default:
      return 2;
  }
}
function startFlowGraph(rt) {
  if (subscribeFlowGraph(rt)) {
    fireFlowGraphStart(rt);
  }
}
function startFlowGraphs(runtimes, isActive = () => true) {
  const pendingStart = runtimes.filter(subscribeFlowGraph);
  pendingStart.filter(isActive).forEach(fireFlowGraphStart);
}
function subscribeFlowGraph(rt) {
  if (rt.started) {
    return false;
  }
  const { context: ctx, env } = rt;
  const eventBlocks = rt.graph.blocks.filter((b) => b.event !== void 0);
  eventBlocks.sort((a, b) => eventInitPriority(a.event) - eventInitPriority(b.event));
  for (const block of eventBlocks) {
    if (block.event === FgEventType.Start || block.event === FgEventType.Tick) {
      continue;
    }
    const def = env.defs[block.type];
    const unsub = subscribeFgEvent(
      env.events,
      block.event,
      (payload) => {
        ctx.executionVariables[`${block.id}:lastEvent`] = payload;
        def?.execute?.(block, ctx, env, block.event);
      },
      rt
    );
    rt._unsub.push(unsub);
  }
  rt.started = true;
  return true;
}
function fireFlowGraphStart(rt) {
  pumpFlowGraphLifecycle(rt, FgEventType.Start, { event: "/extensions/KHR_interactivity/events/sceneReady" });
}
function pumpFlowGraphLifecycle(rt, event, payload) {
  const { context: ctx, env } = rt;
  const handlers = rt.graph.blocks.filter((block) => block.event === event).map((block) => (eventPayload) => {
    ctx.executionVariables[`${block.id}:lastEvent`] = eventPayload;
    env.defs[block.type]?.execute?.(block, ctx, env, event);
  });
  pumpFgEventHandlers(env.events, handlers, payload);
}
function pumpFlowGraphEvent(rt, event, payload) {
  if (rt.started) {
    pumpFlowGraphLifecycle(rt, event, payload);
  }
}
function pumpFlowGraphTick(rt, deltaMs) {
  pumpFlowGraphLifecycle(rt, FgEventType.Tick, {
    deltaMs,
    deltaTime: deltaMs / 1e3,
    event: "/extensions/KHR_interactivity/events/sceneTick"
  });
}
function tickFlowGraph(rt, deltaMs) {
  flushFgEvents(rt.env.events);
  if (!rt.started) {
    startFlowGraph(rt);
  }
  pumpFlowGraphTick(rt, deltaMs);
  advanceFlowGraphTasks(rt, deltaMs);
}
function advanceFlowGraphTasks(rt, deltaMs) {
  const { context: ctx, env } = rt;
  const snapshot = ctx.pending.slice();
  for (const task of snapshot) {
    if (task.canceled || !stillPending(ctx, task)) {
      continue;
    }
    const block = blockById(env.graph, task.blockId);
    if (block) {
      env.defs[block.type]?.onTick?.(block, ctx, env, deltaMs, task);
    }
  }
  compactPending(ctx);
}
function disposeFlowGraph(rt) {
  const { context: ctx, env } = rt;
  for (const unsub of rt._unsub) {
    unsub();
  }
  rt._unsub.length = 0;
  const visited = /* @__PURE__ */ new Set();
  for (const task of ctx.pending) {
    task.canceled = true;
    if (!visited.has(task.blockId)) {
      visited.add(task.blockId);
      const block = blockById(env.graph, task.blockId);
      if (block) {
        env.defs[block.type]?.cancelPending?.(block, ctx, env);
      }
    }
  }
  ctx.pending.length = 0;
  for (const key of Object.keys(ctx.connectionValues)) {
    delete ctx.connectionValues[key];
  }
  for (const key of Object.keys(ctx.executionVariables)) {
    delete ctx.executionVariables[key];
  }
  rt.started = false;
}
function blockById(graph, id) {
  const index = graph.byId[id];
  return index === void 0 ? void 0 : graph.blocks[index];
}

export { activateSignal, addPending, advanceFlowGraphTasks, cancelPendingForBlock, compactPending, createFgContext, createFgEnv, createFgRuntime, disposeFlowGraph, getDataValue, getExecVar, pumpFlowGraphEvent, pumpFlowGraphTick, setDataValue, setExecVar, startFlowGraph, startFlowGraphs, stillPending, tickFlowGraph };
//# sourceMappingURL=runtime.js.map
